import { SEARCH_LONG_RUNNING_TASKS } from "adapters/ehr.service";
import { toast } from "react-toastify";
import {
  calculateProgress,
  getTaskMessage,
  TaskProcessorRegistry,
  extractTemplateName,
  extractSOAPNoteTitle,
} from "config/helpers/backgroundTaskUtils/backgroundTaskUtils";
import { getUnixMs } from "config/helpers/date_helper";
import { updateProgressState, resetProgressState, refreshPatientHistoryWindow } from "../actions/recognizerActions";
import { handleSOAPTaskCompletion } from "config/helpers/handleSOAPTaskCompletion";
import { extractPatientAndEncounterIdFromComposition } from "config/helpers/utils";
import { constants } from "../constants/redux-constants";
import { removeActiveSOAPGeneration } from "../actions/activeSOAPGenerationActions";
import { TASK_STATUS } from "config/constants/constants";

const POLL_INTERVAL = 2000;
const MAX_IDLE_MINUTES = 10;
const TASK_QUERY = "Task?code=http://enliv.com/fhir/coding/task/code/process-code|long-running-operation&_sort=-_lastUpdated";


const watchedTasks = new Map();

// Single global poller
let globalPollerInterval = null;
let abortController = null;

const startGlobalPoller = (dispatch, getState) => {
  // If already running, don't start another
  if (globalPollerInterval) {
    return;
  }

  abortController = new AbortController();

  globalPollerInterval = setInterval(async () => {
    // If no tasks are being watched, stop polling
    if (watchedTasks.size === 0) {
      stopGlobalPoller();
      return;
    }

    try {
      const signal = abortController.signal;

      // Single API call to fetch all long-running tasks
      const allTasks = await SEARCH_LONG_RUNNING_TASKS(TASK_QUERY, signal);

      if (!Array.isArray(allTasks)) {
        console.warn("Invalid response from SEARCH_LONG_RUNNING_TASKS");
        return;
      }

      // Create a map for quick lookup
      const taskMap = new Map();
      allTasks.forEach(task => {
        if (task?.id) {
          taskMap.set(String(task.id), task);
        }
      });

      // Process each watched task
      const tasksToRemove = [];

      for (const [taskId, meta] of watchedTasks.entries()) {
        const serverTask = taskMap.get(String(taskId));

        // Handle missing task (stale/timed out)
        if (!serverTask) {
          handleMissingTask(dispatch, getState, taskId, meta, tasksToRemove);
          continue;
        }

        // Process the task update
        processTaskUpdate(dispatch, getState, taskId, meta, serverTask, tasksToRemove);
      }

      // Remove completed/failed/stale tasks
      tasksToRemove.forEach(taskId => {
        watchedTasks.delete(taskId);
      });

    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Global task polling failed:", err);
      }
    }
  }, POLL_INTERVAL);
};

/**
 * Stop the global poller
 */
const stopGlobalPoller = () => {
  if (globalPollerInterval) {
    clearInterval(globalPollerInterval);
    globalPollerInterval = null;
  }

  if (abortController) {
    abortController.abort();
    abortController = null;
  }
};

/**
 * Handle missing/stale tasks
 */
const handleMissingTask = (dispatch, getState, taskId, meta, tasksToRemove) => {
  const taskAge = Date.now() - meta.addedOn;

  if (taskAge > MAX_IDLE_MINUTES * 60 * 1000) {
    console.warn(`Task ${taskId} is stale (${MAX_IDLE_MINUTES}min timeout), removing from watcher.`);

    // Update local task state
    if (meta.taskState) {
      meta.taskState.status = TASK_STATUS.FAILED;
      meta.taskState.message = "Task timed out.";
    }

    handleTaskCleanup(dispatch, getState, taskId, meta, TASK_STATUS.FAILED);
    tasksToRemove.push(taskId);
  }
};

/**
 * Process a single task update
 */
const processTaskUpdate = (dispatch, getState, taskId, meta, serverTask, tasksToRemove) => {
  const processor = TaskProcessorRegistry[meta.taskCategory];
  const storeTask = meta.taskState;

  if (!processor || !storeTask) {
    console.error(`Missing processor or store task for ${taskId}`);
    tasksToRemove.push(taskId);
    return;
  }

  try {
    const taskData = processor.parse(serverTask);
    const newStatus = taskData.status;
    const newProgress = calculateProgress(taskData, storeTask.progress);
    const newMessage = getTaskMessage(taskData, meta.taskCategory);

    let newTitle = storeTask.title;

    // Update title on completion
    if (newStatus === TASK_STATUS.COMPLETED) {
      if (meta.taskCategory === "template") {
        const templateName = extractTemplateName(taskData);
        if (templateName) {
          newTitle = templateName;
        }
      } else if (meta.taskCategory === "soap") {
        const soapTitle = extractSOAPNoteTitle(taskData);
        if (soapTitle) {
          newTitle = soapTitle;
        }
      }
    }

    // Update store task
    Object.assign(storeTask, {
      ...taskData,
      status: newStatus,
      progress: newProgress,
      message: newMessage,
      title: newTitle,
      lastUpdated: Date.now(),
      isRead: false,
    });

    // Handle category-specific logic
    if (meta.taskCategory === "soap") {
      handleSOAPTaskUpdate(dispatch, getState, taskId, meta, serverTask, taskData, newStatus, newProgress, newMessage, tasksToRemove);
    } else if (meta.taskCategory === "template") {
      handleTemplateTaskUpdate(dispatch, getState, taskId, meta, serverTask, taskData, newStatus, tasksToRemove);
    }
  } catch (err) {
    console.error(`Error processing task ${taskId}:`, err);

    if (meta.taskState) {
      meta.taskState.status = TASK_STATUS.FAILED;
      meta.taskState.message = "Failed to process task.";
    }

    handleTaskCleanup(dispatch, getState, taskId, meta, TASK_STATUS.FAILED);
    tasksToRemove.push(taskId);
  }
};

/**
 * Handle SOAP task updates
 */
const handleSOAPTaskUpdate = (dispatch, getState, taskId, meta, serverTask, taskData, newStatus, newProgress, newMessage, tasksToRemove) => {
  const processor = TaskProcessorRegistry[meta.taskCategory];
  const storeTask = meta.taskState;
  const finalPayload = processor.extractFinalPayload(serverTask);

  let { soapPatientId, soapEncounterId } = extractPatientAndEncounterIdFromComposition(finalPayload);

  if (!soapPatientId || !soapEncounterId) {
    soapPatientId = serverTask?.for?.reference?.replace("Patient/", "");
    soapEncounterId = serverTask?.encounter?.reference?.replace("Encounter/", "");
  }

  // Update progress during processing
  if ([TASK_STATUS.IN_PROGRESS, TASK_STATUS.QUEUED, TASK_STATUS.PENDING].includes(newStatus)) {
    dispatch(
      updateProgressState({
        patientId: soapPatientId,
        encounterId: soapEncounterId,
        isProgressReady: true,
        progress: Math.round(newProgress),
        progressMessage: newMessage,
        step: taskData.processedTaskGroups,
        totalSteps: taskData.totalTaskGroups,
      })
    );
  }

  // Handle completion
  if (newStatus === TASK_STATUS.COMPLETED) {
    // Prevent duplicate processing
    if (storeTask.processedCompletion) {
      tasksToRemove.push(taskId);
      return;
    }

    storeTask.processedCompletion = true;

    const activeGenerations = getState().activeSOAPGeneration?.activeGenerations || [];
    const matchedGeneration = activeGenerations.find(
      g => g.patientId === soapPatientId && g.encounterId === soapEncounterId
    );
    const patientName = matchedGeneration?.patientName || "";

    // Update local task state with payload
    storeTask.payload = {
      ...finalPayload,
      patientId: soapPatientId,
      encounterId: soapEncounterId,
    };

    const soapNoteTitle = finalPayload?.code?.text;
    const defaultClinicalNoteTitle = finalPayload?.title;

    handleSOAPTaskCompletion({
      dispatch,
      getState,
      setRecognitionEvents: typeof meta.setRecognitionEvents === "function" ? meta.setRecognitionEvents : () => { },
      soapPatientId,
      soapEncounterId,
      soapNoteTitle,
      defaultClinicalNoteTitle,
      practitionerId: storeTask.practitionerId,
      patientName: patientName,
      isSuccess: true,
    });

    handleTaskCleanup(dispatch, getState, taskId, meta, TASK_STATUS.COMPLETED, soapPatientId, soapEncounterId);
    tasksToRemove.push(taskId);
  }

  // Handle failure
  if (newStatus === TASK_STATUS.FAILED) {
    // Prevent duplicate processing
    if (storeTask.processedFailure) {
      tasksToRemove.push(taskId);
      return;
    }

    storeTask.processedFailure = true;

    toast.error(taskData?.error, {
      data: { ignoreNotification: true },
    });

    handleTaskCleanup(dispatch, getState, taskId, meta, TASK_STATUS.FAILED, soapPatientId, soapEncounterId);
    tasksToRemove.push(taskId);
  }
};

const handleTemplateTaskUpdate = (dispatch, getState, taskId, meta, serverTask, taskData, newStatus, tasksToRemove) => {
  const processor = TaskProcessorRegistry[meta.taskCategory];
  const storeTask = meta.taskState;

  // Handle completion
  if (newStatus === TASK_STATUS.COMPLETED) {
    // Prevent duplicate processing
    if (storeTask.processedCompletion) {
      tasksToRemove.push(taskId);
      return;
    }

    storeTask.processedCompletion = true;

    try {
      const finalPayload = processor.extractFinalPayload(serverTask);
      const templateName = extractTemplateName(taskData);

      // Update local task state with payload
      storeTask.payload = finalPayload;

      toast.success(`${templateName} generated successfully!`, {
        data: { ignoreNotification: true },
      });
    } catch (err) {
      console.error(`Processor failed to extract final payload for ${taskId}:`, err);
      storeTask.status = TASK_STATUS.FAILED;
      storeTask.message = "Failed to process result.";
    }

    tasksToRemove.push(taskId);
  }

  // Handle failure
  if (newStatus === TASK_STATUS.FAILED) {
    // Prevent duplicate processing
    if (storeTask.processedFailure) {
      tasksToRemove.push(taskId);
      return;
    }

    storeTask.processedFailure = true;

    toast.error(taskData?.error, {
      data: { ignoreNotification: true },
    });

    tasksToRemove.push(taskId);
  }
};

/**
 * Helper function to handle task cleanup
 */
const handleTaskCleanup = (dispatch, getState, taskId, meta, status, patientId = null, encounterId = null) => {
  if (meta.taskCategory === "soap") {
    if (!patientId || !encounterId) {
      const storeTask = meta.taskState;
      patientId = storeTask?.patientId;
      encounterId = storeTask?.encounterId;
    }

    if (patientId && encounterId) {
      dispatch(
        resetProgressState({
          patientId,
          encounterId,
        })
      );

      dispatch({
        type: constants.RECOGNIZER.STOP_RECORDING,
        payload: { patientId, encounterId },
      });

      dispatch({
        type: constants.RECOGNIZER.RESET_RECOGNIZER_FOR_ENCOUNTER,
      });

      dispatch(
        removeActiveSOAPGeneration({
          patientId,
          encounterId,
        })
      );

      refreshPatientHistoryWindow({
        setRecognitionEvents: meta.setRecognitionEvents,
        dispatch,
      });
    }
  }
};

/**
 * PUBLIC API: Start watching a task
 */
export const startWatchingTask = (taskId, taskCategory = "generic", setRecognitionEvents) => async (dispatch, getState) => {
  const PractitionerID = getState().auth?.practitionerData?.id;
  const patientName = getState().patientObj?.patientObj?.name?.[0]?.text || "";
  const patientId = getState().patientObj?.patientObj?.id || "";
  const encounterId = getState().encounterObj?.encounter?.id;

  if (!taskId || !TaskProcessorRegistry[taskCategory]) {
    console.error(`Cannot watch task: Invalid taskId or unknown category "${taskCategory}"`);
    return;
  }

  // If already watching this task, don't add it again
  if (watchedTasks.has(taskId)) {
    console.log(`Task ${taskId} is already being watched`);
    return;
  }

  const processor = TaskProcessorRegistry[taskCategory];
  const initialState = processor.getInitialState(taskId);

  // Store task state locally
  const taskState = {
    ...initialState,
    practitionerId: PractitionerID,
    type: "background_task",
    task_category: taskCategory,
    addedOn: getUnixMs(),
    lastUpdated: Date.now(),
    patientName,
    patientId,
    encounterId,
  };

  watchedTasks.set(taskId, {
    taskCategory,
    addedOn: Date.now(),
    taskState,
    setRecognitionEvents: typeof setRecognitionEvents === "function" ? setRecognitionEvents : () => { },
  });

  // Start global poller if not already running
  startGlobalPoller(dispatch, getState);
};

/**
 * PUBLIC API: Stop watching a specific task
 */
export const stopWatchingTask = (taskId) => (dispatch) => {
  watchedTasks.delete(taskId);

  // If no more tasks, stop global poller
  if (watchedTasks.size === 0) {
    stopGlobalPoller();
  }
};

/**
 * PUBLIC API: Stop all polling tasks
 */
export const stopAllPollingTasks = () => (dispatch) => {
  watchedTasks.clear();
  stopGlobalPoller();
};

/**
 * PUBLIC API: Get current task state
 */
export const getWatchedTaskState = (taskId) => {
  const meta = watchedTasks.get(taskId);
  return meta?.taskState || null;
};

/**
 * PUBLIC API: Get all watched tasks
 */
export const getAllWatchedTasks = () => {
  const tasks = [];
  watchedTasks.forEach((meta, taskId) => {
    if (meta.taskState) {
      tasks.push(meta.taskState);
    }
  });
  return tasks;
};